import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import confetti from 'canvas-confetti';
import BattleshipBoard from './BattleshipBoard';
import {
  createInitialGameState,
  createEmptyGrid,
  placeShip,
  randomPlaceShips,
  allShipsPlaced,
  processAttack,
  markPlayerReady,
  SHIPS,
} from './logic-v2';
import {
  subscribeToGame,
  updateGameState,
  updateGameStatus,
  requestRematch,
  resetGameForRematch,
  saveGameToHistory,
  updateMatchStats,
} from '../../services/gameService';
import { addRecentOpponent } from '../../services/friendService';
import { database } from '../../services/firebase';
import { ref, update, serverTimestamp } from 'firebase/database';
import './Battleship.css';

// Helper function to safely get a string value (prevents rendering objects)
const safeString = (value, fallback = '') => {
  if (value === null || value === undefined) return fallback;
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  return fallback;
};

// Helper function to sanitize game state for Firebase (replace undefined with null)
const sanitizeForFirebase = (obj) => {
  if (obj === undefined) return null;
  if (obj === null) return null;
  if (Array.isArray(obj)) {
    return obj.map(item => sanitizeForFirebase(item));
  }
  if (typeof obj === 'object') {
    const sanitized = {};
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        sanitized[key] = sanitizeForFirebase(obj[key]);
      }
    }
    return sanitized;
  }
  return obj;
};

// Convert Firebase objects back to proper arrays - PRESERVE ALL DATA
const normalizeGridFromFirebase = (grid) => {
  if (!grid) return createEmptyGrid();

  const arrayGrid = [];

  for (let i = 0; i < 10; i++) {
    const row = [];
    const sourceRow = grid[i];

    for (let j = 0; j < 10; j++) {
      // Preserve any existing value, even if row structure is weird
      let cellValue = null;

      if (sourceRow !== null && sourceRow !== undefined) {
        if (Array.isArray(sourceRow)) {
          cellValue = sourceRow[j] !== undefined ? sourceRow[j] : null;
        } else if (typeof sourceRow === 'object') {
          cellValue = sourceRow[j] !== undefined ? sourceRow[j] : null;
        }
      }

      row.push(cellValue);
    }

    arrayGrid.push(row);
  }

  return arrayGrid;
};

const Battleship = ({ roomCode, roomData, user }) => {
  const navigate = useNavigate();
  const [gameState, setGameState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [myShips, setMyShips] = useState([]);
  const [selectedShip, setSelectedShip] = useState(SHIPS[0]);
  const [ready, setReady] = useState(false);
  const [showOrientationPopup, setShowOrientationPopup] = useState(false);
  const [pendingPlacement, setPendingPlacement] = useState(null);
  const [rematchRequested, setRematchRequested] = useState(false);
  const [opponentRematchRequested, setOpponentRematchRequested] = useState(false);
  const [error, setError] = useState(null);
  const [actionLog, setActionLog] = useState([]);
  const confettiShownRef = useRef(false);
  const gameRecordedRef = useRef(false);
  const lastProcessedAttackRef = useRef(null);
  const shipsRestoredRef = useRef(false);

  // Validate props
  if (!roomCode || !roomData || !user) {
    return (
      <div className="battleship-container">
        <div className="error-message">Missing required data. Please try again.</div>
      </div>
    );
  }

  const isHost = roomData.host && roomData.host.userId === user.uid;
  const opponent = isHost ? roomData.guest : roomData.host;

  // If opponent left, show waiting state
  if (!opponent) {
    const handleReturnToLobby = async () => {
      try {
        const roomRef = ref(database, `rooms/${roomCode}`);
        await update(roomRef, {
          status: 'waiting',
          gameSelected: null,
          lastActivity: serverTimestamp()
        });
        navigate(`/room/${roomCode}`);
      } catch (error) {
        console.error('Error returning to lobby:', error);
        navigate(`/room/${roomCode}`);
      }
    };

    return (
      <div className="battleship-container">
        <div className="game-header">
          <h2>Battleship</h2>
        </div>
        <div className="player-waiting-state">
          <div className="waiting-spinner"></div>
          <h3>Player left the room</h3>
          <p>Waiting for player to return...</p>
          <p className="waiting-hint">The game will resume when they come back</p>
          <button className="leave-button" onClick={handleReturnToLobby}>
            Return to Lobby
          </button>
        </div>
      </div>
    );
  }

  const isMyTurn = gameState && gameState.currentTurn === user.uid;
  const isPlacementPhase = gameState && gameState.phase === 'placement';
  const isBattlePhase = gameState && gameState.phase === 'battle';

  // Subscribe to game updates
  useEffect(() => {
    try {
      const unsubscribe = subscribeToGame(roomCode, async (game) => {
        if (game && game.gameState) {
          // Log incoming Firebase data
          console.log('📥 Firebase update - hostAttacks type:', Array.isArray(game.gameState.hostAttacks) ? 'array' : typeof game.gameState.hostAttacks);
          console.log('📥 Firebase update - guestAttacks type:', Array.isArray(game.gameState.guestAttacks) ? 'array' : typeof game.gameState.guestAttacks);

          // Normalize grids from Firebase format to arrays
          const normalizedGameState = {
            ...game.gameState,
            hostAttacks: normalizeGridFromFirebase(game.gameState.hostAttacks),
            guestAttacks: normalizeGridFromFirebase(game.gameState.guestAttacks)
          };

          // Log normalized data
          const hostAttackCount = normalizedGameState.hostAttacks.flat().filter(c => c !== null).length;
          const guestAttackCount = normalizedGameState.guestAttacks.flat().filter(c => c !== null).length;
          console.log('✅ After normalization - host attacks:', hostAttackCount, 'guest attacks:', guestAttackCount);

          setGameState(normalizedGameState);

          // Restore myShips from Firebase if we're in battle phase and haven't restored yet
          // This handles the case where a player refreshes mid-game
          if (normalizedGameState.phase === 'battle' && !shipsRestoredRef.current) {
            const savedShips = isHost ? normalizedGameState.hostShips : normalizedGameState.guestShips;
            if (savedShips && savedShips.length > 0) {
              setMyShips(savedShips);
              shipsRestoredRef.current = true;
            }
          }

          // Update ready status
          const playerReady = isHost ? game.gameState.hostReady : game.gameState.guestReady;
          setReady(playerReady);

          // Check rematch requests
          if (game.rematchRequests && opponent) {
            const myRequest = !!game.rematchRequests[user.uid];
            const opponentRequest = !!game.rematchRequests[opponent.userId];

            setRematchRequested(myRequest);
            setOpponentRematchRequested(opponentRequest);

            // If both players requested rematch, only the host resets the game
            if (myRequest && opponentRequest && isHost && game.gameState.isGameOver) {
              const newGameState = createInitialGameState(
                roomData.host.userId,
                roomData.guest.userId
              );

              await resetGameForRematch(
                roomCode,
                'battleship',
                newGameState,
                {
                  host: roomData.host,
                  guest: roomData.guest,
                }
              );

              // Reset local state
              setMyShips([]);
              setReady(false);
              shipsRestoredRef.current = false;
            }
          } else {
            setRematchRequested(false);
            setOpponentRematchRequested(false);
          }
        } else if (game === null) {
          setError('Game not found. It may not have been created yet.');
        } else if (game && !game.gameState) {
          setError('Game data is missing gameState. Please try restarting the game.');
        }

        setLoading(false);
      });

      return () => unsubscribe();
    } catch (err) {
      console.error('Error setting up game subscription:', err);
      setError(err.message);
      setLoading(false);
    }
  }, [roomCode, user.uid, opponent?.userId, isHost, roomData]);

  // Trigger confetti on win
  useEffect(() => {
    if (gameState && gameState.isGameOver && !confettiShownRef.current) {
      const iWon = gameState.winner === user.uid;

      if (iWon) {
        confetti({
          particleCount: 150,
          spread: 80,
          origin: { y: 0.6 },
        });
        confettiShownRef.current = true;
      }
    }

    // Reset flags when game restarts
    if (gameState && !gameState.isGameOver && gameState.phase === 'placement') {
      confettiShownRef.current = false;
      gameRecordedRef.current = false;
      shipsRestoredRef.current = false;
    }
  }, [gameState, user.uid]);

  // Track action log
  useEffect(() => {
    if (gameState && gameState.lastAttack) {
      // Create unique key for this attack
      const attackKey = `${gameState.lastAttack.attacker}-${gameState.lastAttack.row}-${gameState.lastAttack.col}-${gameState.lastAttack.result}`;

      // Only add if this is a new attack
      if (lastProcessedAttackRef.current !== attackKey) {
        lastProcessedAttackRef.current = attackKey;

        const time = new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
        const attacker = gameState.lastAttack.attacker === user.uid ? 'You' : safeString(opponent?.displayName, 'Opponent');
        const result = gameState.lastAttack.result === 'hit' ? 'HIT!' : 'MISSED.';
        const sunk = gameState.lastAttack.sunkShip ? ` Sunk ${safeString(gameState.lastAttack.sunkShip)}!` : '';
        const message = `${attacker} just shot at ${String.fromCharCode(65 + gameState.lastAttack.col)}${gameState.lastAttack.row + 1}: ${result}${sunk}`;

        setActionLog(prev => [
          { time, message },
          ...prev.slice(0, 9) // Keep last 10 actions
        ]);
      }
    }
  }, [gameState?.lastAttack, user.uid, opponent?.displayName]);

  // Save game to history when it ends
  useEffect(() => {
    if (gameState && gameState.isGameOver && !gameRecordedRef.current && opponent) {
      gameRecordedRef.current = true;

      const iWon = gameState.winner === user.uid;
      const result = iWon ? 'won' : 'lost';

      saveGameToHistory(user.uid, {
        gameType: 'battleship',
        opponent: safeString(opponent.displayName, 'Unknown'),
        opponentId: opponent.userId || 'unknown',
        result,
        winner: gameState.winner,
        roomCode,
      });

      updateMatchStats(user.uid, 'battleship', result);

      if (opponent.userId) {
        addRecentOpponent(user.uid, {
          userId: opponent.userId,
          displayName: safeString(opponent.displayName, 'Unknown'),
          photoURL: opponent.photoURL || '',
          lastGame: 'battleship',
          lastResult: result,
        });
      }

      updateGameStatus(roomCode, 'completed');
    }
  }, [gameState, user.uid, opponent, roomCode]);

  const handlePlacementClick = (row, col) => {
    if (!selectedShip || ready) return;

    // Show orientation popup instead of immediately placing
    setPendingPlacement({ row, col });
    setShowOrientationPopup(true);
  };

  const handleOrientationSelect = (horizontal) => {
    if (!pendingPlacement || !selectedShip) return;

    const { row, col } = pendingPlacement;
    const newShips = placeShip(myShips, selectedShip.id, row, col, horizontal);
    setMyShips(newShips);

    // Auto-select next ship if successful placement
    if (newShips.length > myShips.length) {
      const placedShipIds = newShips.map(s => s.id);
      const nextShip = SHIPS.find(s => !placedShipIds.includes(s.id));
      setSelectedShip(nextShip || null);
    }

    // Close popup
    setShowOrientationPopup(false);
    setPendingPlacement(null);
  };

  const handleAttackClick = async (row, col) => {
    if (!isBattlePhase || !isMyTurn || gameState.isGameOver) return;

    // Check if already attacked
    const myAttacks = isHost ? gameState.hostAttacks : gameState.guestAttacks;

    // Safety check: ensure attack grid exists
    if (!myAttacks || !Array.isArray(myAttacks) || myAttacks.length !== 10) {
      console.error('Attack grid is not initialized properly - grid invalid', { myAttacks, row, col });
      setError('Game state error. Please refresh and try again.');
      return;
    }

    if (!myAttacks[row] || !Array.isArray(myAttacks[row]) || myAttacks[row].length !== 10) {
      console.error('Attack grid is not initialized properly - row invalid', {
        myAttacks,
        row,
        col,
        rowData: myAttacks[row],
        rowType: typeof myAttacks[row],
        isArray: Array.isArray(myAttacks[row])
      });
      setError('Game state error. Please refresh and try again.');
      return;
    }

    if (myAttacks[row][col] !== null) return;

    // Process attack - logic-v2 handles all deep copying internally
    const newGameState = processAttack(gameState, user.uid, row, col);

    if (newGameState.error) {
      setError(newGameState.error);
      return;
    }

    // Sanitize for Firebase (remove any undefined values)
    const sanitizedGameState = sanitizeForFirebase(newGameState);

    // Log the attack for debugging with actual grid data
    const attackGrid = isHost ? sanitizedGameState.hostAttacks : sanitizedGameState.guestAttacks;
    console.log('Processing attack:', { row, col, isHost });
    console.log('Attack count:', attackGrid.flat().filter(cell => cell !== null).length);
    console.log('Attack grid sample:', attackGrid.slice(0, 3).map(r => r.slice(0, 5)));

    const success = await updateGameState(roomCode, sanitizedGameState);
    if (!success) {
      console.error('Failed to update game state');
      setError('Failed to record attack. Please try again.');
    }
  };

  const handleRandomPlacement = () => {
    if (ready) return;
    const ships = randomPlaceShips();
    setMyShips(ships);
    setSelectedShip(null);
  };

  const handleClearShips = () => {
    if (ready) return;
    setMyShips([]);
    setSelectedShip(SHIPS[0]);
  };

  const handleReady = async () => {
    if (!allShipsPlaced(myShips)) {
      setError('Please place all ships before starting!');
      return;
    }

    // Validate ships data to prevent undefined errors
    try {
      console.log('Original myShips:', JSON.stringify(myShips, null, 2));

      const validatedShips = myShips.map(ship => {
        if (!ship || !ship.id || !ship.positions) {
          console.error('Invalid ship data:', ship);
          throw new Error('Invalid ship data detected');
        }

        // Ensure all position values are defined
        const validatedPositions = ship.positions.map(pos => ({
          row: typeof pos.row === 'number' ? pos.row : 0,
          col: typeof pos.col === 'number' ? pos.col : 0,
          hit: Boolean(pos.hit),
        }));

        return {
          id: ship.id || '',
          name: ship.name || '',
          size: typeof ship.size === 'number' ? ship.size : 0,
          row: typeof ship.row === 'number' ? ship.row : 0,
          col: typeof ship.col === 'number' ? ship.col : 0,
          isHorizontal: Boolean(ship.isHorizontal),
          positions: validatedPositions,
          sunk: Boolean(ship.sunk),
        };
      }).filter(ship => ship.id && ship.positions.length > 0);

      console.log('Validated ships:', JSON.stringify(validatedShips, null, 2));

      const newGameState = markPlayerReady(gameState, user.uid, validatedShips);

      // Sanitize for Firebase
      const sanitizedGameState = sanitizeForFirebase(newGameState);

      console.log('New game state before update:', JSON.stringify(sanitizedGameState, null, 2));

      await updateGameState(roomCode, sanitizedGameState);
      setReady(true);
    } catch (err) {
      console.error('Error in handleReady:', err);
      setError('Failed to start game. Please try placing your ships again.');
    }
  };

  const handleRematch = async () => {
    await requestRematch(roomCode, user.uid);
  };

  const handleLeaveGame = async () => {
    try {
      const roomRef = ref(database, `rooms/${roomCode}`);
      await update(roomRef, {
        status: 'waiting',
        gameSelected: null,
        'host/gameSelection': null,
        'guest/gameSelection': null,
        lastActivity: serverTimestamp()
      });
      navigate(`/room/${roomCode}`);
    } catch (error) {
      console.error('Error leaving game:', error);
      navigate(`/room/${roomCode}`);
    }
  };

  if (loading) {
    return (
      <div className="battleship-container">
        <div className="loading-message">
          <div className="spinner-large"></div>
          <p>Loading game...</p>
        </div>
      </div>
    );
  }

  if (error && !gameState) {
    return (
      <div className="battleship-container">
        <div className="error-message">{safeString(error, 'An error occurred')}</div>
      </div>
    );
  }

  if (!gameState) {
    return (
      <div className="battleship-container">
        <div className="loading-message">Waiting for game to start...</div>
      </div>
    );
  }

  const iWon = gameState.isGameOver && gameState.winner === user.uid;
  const iLost = gameState.isGameOver && gameState.winner !== user.uid;

  const opponentReady = isHost ? gameState.guestReady : gameState.hostReady;
  const myAttacks = isHost ? gameState.hostAttacks : gameState.guestAttacks;
  const opponentAttacks = isHost ? gameState.guestAttacks : gameState.hostAttacks;

  return (
    <div className="battleship-container">
      {/* Header */}
      <div className="game-header">
        <h1 className="game-title">⚓ Battleship 🎯</h1>
      </div>

      {/* Placement Phase */}
      {isPlacementPhase && (
        <div className="placement-phase">
          <h2>Place Your Ships</h2>

          {!ready ? (
            <>
              {/* Ship Selection */}
              <div className="ship-selection">
                {SHIPS.map(ship => {
                  const isPlaced = myShips.some(s => s.id === ship.id);
                  const isSelected = selectedShip && selectedShip.id === ship.id;

                  return (
                    <button
                      key={ship.id}
                      className={`ship-button ${isPlaced ? 'placed' : ''} ${isSelected ? 'selected' : ''}`}
                      onClick={() => !isPlaced && setSelectedShip(ship)}
                    >
                      <span className="ship-name">{ship.name}</span>
                      <span className="ship-size">({ship.size})</span>
                      {isPlaced && <span className="checkmark">✓</span>}
                    </button>
                  );
                })}
              </div>

              {/* Controls */}
              <div className="placement-controls">
                <button
                  className="control-button"
                  onClick={handleRandomPlacement}
                >
                  🎲 Random
                </button>
                <button
                  className="control-button"
                  onClick={handleClearShips}
                >
                  🗑️ Clear
                </button>
              </div>

              {/* Board */}
              <BattleshipBoard
                myShips={myShips}
                opponentAttacks={null}
                onCellClick={handlePlacementClick}
                isPlacementPhase={true}
                disabled={false}
              />

              {/* Ready Button */}
              {allShipsPlaced(myShips) && (
                <button className="ready-button" onClick={handleReady}>
                  ✓ Ready to Battle!
                </button>
              )}
            </>
          ) : (
            <div className="waiting-opponent">
              <div className="waiting-spinner"></div>
              <p>Waiting for {safeString(opponent?.displayName, 'opponent')} to place ships...</p>
              {opponentReady && <p>Both players ready! Starting battle...</p>}
            </div>
          )}
        </div>
      )}

      {/* Battle Phase */}
      {isBattlePhase && (
        <div className="battle-phase">
          {/* Turn Indicator */}
          {!gameState.isGameOver && (
            <div className="turn-indicator-section">
              <p className="turn-indicator">
                {isMyTurn ? "Your Turn!" : `${safeString(opponent?.displayName, 'Opponent')}'s Turn`}
              </p>
            </div>
          )}

          {/* Game Status */}
          {!gameState.isGameOver && (
            <div className="game-status">
              <p className="status-text">
                {isMyTurn ? "🎯 Fire!" : `Waiting for ${safeString(opponent?.displayName, 'opponent')}...`}
              </p>
            </div>
          )}

          {/* Dual Grids */}
          <div className="dual-grids">
            {/* Your Board */}
            <div>
              <h3>Your Board</h3>
              <div className="grid-section">
                <BattleshipBoard
                  myShips={myShips}
                  opponentAttacks={opponentAttacks}
                  onCellClick={() => {}}
                  isPlacementPhase={true}
                  disabled={true}
                />
                <div className="ship-status-sidebar">
                  {SHIPS.map(ship => {
                    const myShip = myShips.find(s => s.id === ship.id);
                    const isSunk = myShip && myShip.sunk;
                    return (
                      <div key={ship.id} className="ship-status-item">
                        <div className={`ship-status-checkbox ${myShip ? 'checked' : ''} ${isSunk ? 'sunk' : ''}`}>
                          {myShip && '■'}
                        </div>
                        <span>{ship.name}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Opponent's Board */}
            <div>
              <h3>Opponent's Board</h3>
              <div className="grid-section">
                <BattleshipBoard
                  myShips={[]}
                  myAttacks={myAttacks}
                  onCellClick={handleAttackClick}
                  isPlacementPhase={false}
                  disabled={!isMyTurn || gameState.isGameOver}
                />
                <div className="ship-status-sidebar">
                  {SHIPS.map(ship => {
                    const opponentShipsSunk = isHost ? gameState.guestShipsSunk : gameState.hostShipsSunk;
                    const isSunk = opponentShipsSunk && opponentShipsSunk.includes(ship.id);
                    return (
                      <div key={ship.id} className="ship-status-item">
                        <div className={`ship-status-checkbox ${isSunk ? 'sunk' : ''}`}>
                          {isSunk && '■'}
                        </div>
                        <span>{ship.name}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Action Log */}
          <div className="action-log">
            {actionLog.length === 0 ? (
              <div className="action-log-entry">Waiting for first move...</div>
            ) : (
              actionLog.map((entry, index) => (
                <div key={index} className="action-log-entry">
                  <span className="action-log-time">[{safeString(entry?.time)}]</span>
                  {safeString(entry?.message)}
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Game Over Message */}
      {gameState.isGameOver && (
        <div className="game-over">
          <div className={`result-message ${iWon ? 'won' : 'lost'}`}>
            {iWon ? (
              <>
                <span className="result-icon">🎉</span>
                <h2>Victory!</h2>
                <p>You sunk all enemy ships!</p>
              </>
            ) : (
              <>
                <span className="result-icon">😞</span>
                <h2>Defeated</h2>
                <p>All your ships were sunk!</p>
              </>
            )}
          </div>

          {/* Rematch Section */}
          <div className="rematch-section">
            {!rematchRequested && !opponentRematchRequested && (
              <button className="rematch-button" onClick={handleRematch}>
                🔄 Request Rematch
              </button>
            )}

            {rematchRequested && !opponentRematchRequested && (
              <button className="rematch-button waiting" disabled>
                ⏳ Waiting for opponent...
              </button>
            )}

            {opponentRematchRequested && !rematchRequested && (
              <button className="rematch-button accept" onClick={handleRematch}>
                ✓ Accept Rematch
              </button>
            )}

            {rematchRequested && opponentRematchRequested && (
              <button className="rematch-button starting" disabled>
                🎮 Starting rematch...
              </button>
            )}

            {opponentRematchRequested && !rematchRequested && (
              <p className="rematch-hint">Opponent wants a rematch!</p>
            )}
          </div>
        </div>
      )}

      {/* Orientation Popup */}
      {showOrientationPopup && (
        <div className="orientation-popup-overlay" onClick={() => setShowOrientationPopup(false)}>
          <div className="orientation-popup" onClick={(e) => e.stopPropagation()}>
            <h3>Choose Ship Orientation</h3>
            <div className="orientation-options">
              <button
                className="orientation-option"
                onClick={() => handleOrientationSelect(true)}
              >
                <div className="orientation-visual horizontal">
                  <div className="ship-line"></div>
                </div>
                <span>Horizontal</span>
              </button>
              <button
                className="orientation-option"
                onClick={() => handleOrientationSelect(false)}
              >
                <div className="orientation-visual vertical">
                  <div className="ship-line"></div>
                </div>
                <span>Vertical</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Leave Game Button */}
      <div className="leave-game-section">
        <button className="leave-button" onClick={handleLeaveGame}>
          Leave Game
        </button>
      </div>
    </div>
  );
};

export default Battleship;
